import { Box, Tooltip, Typography } from "@mui/material";
import { formatCompactUsd, formatPercent, formatUsd, OTHER_COLOR } from "./chartTheme";

// Part-to-whole as a ranked list: one thin bar per item, sized by its share
// of the total. Replaces donuts with many slices, which are hard to compare.
const ShareBars = ({
  data,
  total,
  title = "Total",
  valueFormat = formatCompactUsd,
  fullValueFormat = formatUsd,
  color: singleColor,
  emptyText = "No volume data for this period",
}) => {
  // Largest first, "Other" always last
  const items = (data || [])
    .map((item) => ({ ...item, value: Number(item.value) || 0 }))
    .sort((a, b) => (a.id === "other") - (b.id === "other") || b.value - a.value);
  const sum = Number(total) || items.reduce((acc, item) => acc + item.value, 0);

  if (!items.length || !sum) {
    return (
      <Typography variant="body2" color="text.secondary">
        {emptyText}
      </Typography>
    );
  }

  return (
    <Box>
      <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{title}</Typography>
      <Tooltip title={fullValueFormat(sum)} placement="top-start">
        <Typography sx={{ fontSize: 28, fontWeight: 700, color: "text.primary", lineHeight: 1.3, width: "fit-content" }}>
          {valueFormat(sum)}
        </Typography>
      </Tooltip>

      <Box component="ul" sx={{ listStyle: "none", m: 0, p: 0, mt: 2, display: "flex", flexDirection: "column", gap: 1.25 }}>
        {items.map((item) => {
          const share = (item.value / sum) * 100;
          const color =
            item.id === "other" ? OTHER_COLOR : singleColor ?? item.color;
          return (
            <Box component="li" key={item.id}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box
                  component="span"
                  sx={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: color, flexShrink: 0 }}
                />
                <Typography
                  title={item.label}
                  sx={{
                    fontSize: 13,
                    color: item.id === "other" ? "text.secondary" : "text.primary",
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.label}
                </Typography>
                <Tooltip title={fullValueFormat(item.value)} placement="left">
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: "text.primary", fontVariantNumeric: "tabular-nums" }}>
                    {valueFormat(item.value)}
                  </Typography>
                </Tooltip>
                <Typography
                  sx={{ fontSize: 12, color: "text.secondary", width: 44, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                >
                  {formatPercent(share)}
                </Typography>
              </Box>
              <Box sx={{ mt: 0.5, ml: 2, height: 6, borderRadius: 3, backgroundColor: "#f4f6f8" }}>
                <Box
                  sx={{
                    height: "100%",
                    width: `${Math.max(share, 0.5)}%`,
                    borderRadius: 3,
                    backgroundColor: color,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default ShareBars;
